import React, { useState, useEffect } from 'react';
import { 
  Container,
  TextField,
  Card,
  CardContent,
  Typography,
  Avatar,
  Box,
  IconButton,
  Divider,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Backdrop,
  Button,
  Snackbar,
  Modal,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  FavoriteBorder,
  BookmarkBorder,
  Close,
  Public,
} from '@mui/icons-material';
import LaunchIcon from '@mui/icons-material/Launch';
import LoadingAnimation from '../components/LoadingAnimation';
import Layout from '../components/Layout';
import { Favorite, Bookmark } from '@mui/icons-material';
import { jwtDecode } from 'jwt-decode';
import KastLogo from '../kast-fav.png';
import Icon from '../pinIcon.png';
import AvatarIcon from '../avatar.png';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Tooltip from '@mui/material/Tooltip';

const Home = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [userLikes, setUserLikes] = useState({});
  const [userSaves, setUserSaves] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLinkInputActive, setIsLinkInputActive] = useState(false);
  const [likesModal, setLikesModal] = useState({
    open: false,
    postId: null,
    likes: []
  });
  const [user, setUser] = useState({
    displayName: '',
    email: '',
    avatarUrl: '',
    userId: '',
    userEmail: ''
  });
  const [links, setLinks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [linkData, setLinkData] = useState({
    url: '',
    title: '',
    description: '',
    caption: '',
    image: '',
    isPublic: true
  });
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [isFetchingPreview, setIsFetchingPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const KastUser = "Kast User"

  // Sort links by timestamp (newest first)
  const sortedLinks = [...links].sort((a, b) => {
    return new Date(b.timestamp) - new Date(a.timestamp);
  });

  useEffect(() => {
  const handleScroll = () => {
    setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchUserInteractions = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('token');            
        const { uid } = jwtDecode(token);
        
        const likesResponse = await fetch(`https://kast-server-713b1c89d478.herokuapp.com/api/auth/likes/${uid}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const likesData = await likesResponse.json();
        setUserLikes(likesData.likes || {});
        
        const savesResponse = await fetch('https://kast-server-713b1c89d478.herokuapp.com/api/auth/saved-posts', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const savesData = await savesResponse.json();
        const savesMap = savesData.posts.reduce((acc, post) => {
          acc[post.id] = true;
          return acc;
        }, {});
        setUserSaves(savesMap);
      } catch (error) {
        console.error('Error fetching user interactions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserInteractions();
  }, []);

  useEffect(() => {
    fetchLinks();
    fetchUserData();
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleLikePost = async (postId) => {
    try {
      const token = localStorage.getItem('token');    
      
      if (userLikes[postId]) {
        await fetch('https://kast-server-713b1c89d478.herokuapp.com/api/auth/unlike-post', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ linkId: postId })
        });
        
        setUserLikes(prev => {
          const newLikes = { ...prev };
          delete newLikes[postId];
          return newLikes;
        });
        
        setLinks(prev => prev.map(link => {
          if (link.id === postId) {
            return {
              ...link,
              likes: Math.max((link.likes || 0) - 1, 0)
            };
          }
          return link;
        }));
        showSnackbar('Post unliked');
      } else {
        await fetch('https://kast-server-713b1c89d478.herokuapp.com/api/auth/like-post', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ linkId: postId })
        });
        
        setUserLikes(prev => ({
          ...prev,
          [postId]: true
        }));
        
        setLinks(prev => prev.map(link => {
          if (link.id === postId) {
            return {
              ...link,
              likes: (link.likes || 0) + 1
            };
          }
          return link;
        }));
        showSnackbar('Post liked');
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      showSnackbar('Failed to like post', 'error');
    }
  };

  const handleSavePost = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      
      if (userSaves[postId]) {
        await fetch('https://kast-server-713b1c89d478.herokuapp.com/api/auth/unsave-post', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ linkId: postId })
        });
        
        setUserSaves(prev => {
          const newSaves = { ...prev };
          delete newSaves[postId];
          return newSaves;
        });
        showSnackbar('Post unsaved');
      } else {
        await fetch('https://kast-server-713b1c89d478.herokuapp.com/api/auth/save-post', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ linkId: postId })
        });
        
        setUserSaves(prev => ({
          ...prev,
          [postId]: true
        }));
        showSnackbar('Post saved');
      }
    } catch (error) {
      console.error('Error toggling save:', error);
      showSnackbar('Failed to save post', 'error');
    }
  };

  const fetchLikesForPost = async (postId) => {    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://kast-server-713b1c89d478.herokuapp.com/api/auth/post-likes/${postId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();      
      
      if (data.success) {
        setLikesModal({
          open: true,
          postId,
          likes: data.likes
        });
      }
    } catch (error) {
      console.error('Error fetching likes:', error);
    }
  };

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://kast-server-713b1c89d478.herokuapp.com/api/auth/currentUser', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setUser(data);
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const fetchLinks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://kast-server-713b1c89d478.herokuapp.com/api/auth/links', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      const linksArray = Array.isArray(data) ? data : [];
      console.log(linksArray);
      
      setLinks(linksArray);
      setLinks(linksArray);
    } catch (error) {
      console.error('Error fetching links:', error);
      setLinks([]); // Set to empty array on error      
      console.error('Error fetching links:', error);
    }
  };

  const handleUrlChange = async (e) => {
    const url = e.target.value;
    setSearchTerm(url);
    setLinkData(prev => ({ ...prev, url }));
    
    try {
      new URL(url);
      setIsLinkInputActive(true);
      
      if (url.length > 10) {
        setIsFetchingPreview(true);
        const token = localStorage.getItem('token');
        const response = await fetch('https://kast-server-713b1c89d478.herokuapp.com/api/auth/preview-link', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ url })
        });
        
        const data = await response.json();
        console.log("Fetch data", data);
        
        if (response.ok) {
          setPreviewData(data.metadata);
          setLinkData(prev => ({
            ...prev,
            title: data.metadata.title,
            domain: data.metadata.domain,
            siteName: data.metadata.siteName,
            logo: data.metadata.logo
          }));
        }
      }
    } catch {
      setIsLinkInputActive(false);
      setPreviewData(null);
    } finally {
      setIsFetchingPreview(false);
    }
  };

  const handleClearLink = () => {
    setSearchTerm('');
    setLinkData({
      url: '',
      title: '',
      description: '',
      caption: '',
      isPublic: true
    });
    setPreviewData(null);
  };

  const handleCancelLink = () => {
    setIsLinkInputActive(false);
    handleClearLink();
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setLinkData({
      url: '',
      title: '',
      description: '',
      caption: '',
      isPublic: true
    });
    setError(null);
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setLinkData(prev => ({
      ...prev,
      [name]: name === 'isPublic' ? checked : value
    }));
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleSaveLink = async () => {
    if (!linkData.url) {
      setError('URL is required');
      return;
    }        

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('https://kast-server-713b1c89d478.herokuapp.com/api/auth/save-link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          linkData,
          user
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save link');

      handleCloseModal();      
      handleCancelLink();
      fetchLinks();      
      showSnackbar('Link saved');
    } catch (error) {
      setError(error.message);
      showSnackbar(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

    const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    
    if (seconds < 60) return `${seconds} seconds ago`;
    if (seconds < 3600) {
      const minutes = Math.floor(seconds/60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    }
    if (seconds < 86400) {
      const hours = Math.floor(seconds/3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
    const days = Math.floor(seconds/86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  const filteredLinks = Array.isArray(sortedLinks) 
  ? sortedLinks.filter(link => 
      link && 
      (link.url?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (link.userName?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (link.description?.toLowerCase().includes(searchTerm.toLowerCase()))
  ): [];

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds/60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds/3600)}h`;
    return `${Math.floor(seconds/86400)}d`;
  };

  return (        
    <Container maxWidth="md" sx={{ py: 3, position:'relative' }}>
     <Box sx={{      
         position: 'sticky',
        top: isMobile ? 60 : 5,
        zIndex: 1000,
        py: 2,          
      }}>        
          <TextField                              
            variant="outlined"
            placeholder="Search or paste a link to save"
            value={searchTerm}
            onChange={handleUrlChange}
            fullWidth            
            sx={{                 
              "& .MuiOutlinedInput-root": {
                mt:{xs:'20px', md:'0'},                              
                fontSize: isMobile ? '16px' : '18px',
                borderRadius: 4,
                backgroundColor: 'white',
                //pr: 6,
                border: '1.5px solid #74706B',
                p: isMobile ? 0 : 1
              },
              "& fieldset": { 
                border: isLinkInputActive ? '1px solid #e0e0e0' : 'none'
              }
            }}
          />        
      </Box>

      
      {isLoading && <LoadingAnimation />}
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({...prev, open: false}))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>     
      
      
        <Backdrop
          open={isLinkInputActive}
          sx={{ 
            zIndex: 1200,
            backgroundColor: 'rgba(0,0,0,0.1)',
            backdropFilter: 'blur(4px)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0
          }}
          onClick={handleCancelLink}
        />              
        <Box sx={{ 
          mb: 4,
          position: 'relative',
          zIndex: isLinkInputActive ? 1201 : 'auto'
        }}>  
          <Card sx={{
            
          }}>            
            <CardContent sx={{ p: isLinkInputActive ? 2 : '0 !important' }}>             
              <Box sx={{
               
              }}>                               
                
                {isLinkInputActive && (
                  <Box sx={{ 
                                    
                  }}>
                    {isFetchingPreview ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ 
                         
                        }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ 
                              fontWeight: 600, 
                              fontSize: '20px',
                              mb: 0.5
                            }}>
                              {linkData.title || 'No title'}
                            </Typography>                                                                                    
                          </Box>
                          <Box sx={{display:'flex', justifyContent: 'space-between', mb:2}}>
                          <Typography variant="body2" color="text.secondary" sx={{fontWeight: 900, fontSize:'16px'}}>
                              {linkData.siteName || ''}
                            </Typography>
                          
                          {previewData?.logo && (
                            <Avatar 
                              src={previewData.logo} 
                              sx={{ 
                                width: 40, 
                                height: 40,
                                ml: 2
                              }}
                            />
                          )}
                          </Box>
                        </Box>
                        
                        <TextField
                          fullWidth
                          placeholder="Add a caption"
                          variant="outlined"
                          name="caption"
                          value={linkData.caption}
                          onChange={(e) => setLinkData({...linkData, caption: e.target.value})}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              mb:2
                            }
                          }}
                        />
                        
                        <Box sx={{ 
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>                          
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              cursor: 'pointer'
                            }}
                            onClick={() => setLinkData({...linkData, isPublic: !linkData.isPublic})}
                          >
                            <IconButton                             
                              size="small"
                              color={linkData.isPublic ? 'primary' : 'default'}
                              onClick={(e) => {
                                e.stopPropagation();
                                setLinkData({...linkData, isPublic: !linkData.isPublic});
                              }}
                            >
                              {linkData.isPublic ? (
                                <VisibilityIcon fontSize="large" />
                              ) : (
                                <VisibilityOffIcon fontSize="large" />
                              )}
                            </IconButton>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {linkData.isPublic ? 'Public' : 'Private'}
                            </Typography>   
                          </Box>
                          
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button 
                              variant="text" 
                              onClick={handleCancelLink}
                              sx={{ 
                                color: 'text.secondary',
                                textTransform: 'none',
                                fontWeight: 500
                              }}
                            >
                              Cancel
                            </Button>
                            <Button 
                              variant="contained" 
                              onClick={handleSaveLink}
                              disabled={loading}
                              sx={{ 
                                borderRadius: '12px',
                                background: '#FF890A',
                                '&:hover': { background: '#e67a09' },
                                fontWeight: 500,
                                textTransform: 'none',
                                px: 2,
                                py: 1
                              }}
                            >
                              {loading ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                            </Button>
                          </Box>
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Box>                            
            </CardContent>                        
          </Card>          
          </Box>                        

        <Modal open={likesModal.open} onClose={() => setLikesModal({ open: false, postId: null, likes: [] })}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? 300 : 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Likes</Typography>
            <List>
              {likesModal.likes.length > 0 ? (
                likesModal.likes.map(user => (
                  <ListItem key={user.id}>
                    <ListItemAvatar>
                      <Avatar>{user.displayName?.charAt(0) || user.userEmail?.charAt(0)}</Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={user.displayName || user.userEmail.split('@')[0]}                       
                    />
                  </ListItem>
                ))
              ) : (
                <Typography variant="body2">No likes yet</Typography>
              )}
            </List>
          </Box>
        </Modal>

        {!isLoading && (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 3,
          filter: isLinkInputActive ? 'blur(4px)' : 'none',
          transition: 'filter 0.3s ease',
          pointerEvents: isLinkInputActive ? 'none' : 'auto',
          opacity: isLinkInputActive ? 0.9 : 1,
          mt: isMobile ? 5 : -2                                 
        }}>
          <Card  sx={{ borderRadius: 4, backgroundColor:'#FF890A4D', border: '1px solid black' }}>
                <CardContent>                                                  
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                     <Avatar 
                        src={KastLogo}
                        sx={{ width: 40, height: 40, mr:1.5 }}
                      >
                        {KastUser.charAt(0)}
                      </Avatar>                                   
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: 17 }}>
                        Kast
                      </Typography>
                    </Box>
                    <div>
                       <img 
                                src={Icon} 
                                alt="Kast Logo" 
                                style={{ 
                                  height: 35,                                                                                                       
                                }}                                
                              />
                    </div>
                  </Box>
                                    
                    <Typography variant="body2" color="text.secondary" sx={{fontSize: 16, fontWeight: 600}}>
                      Come across something interesting recently? Save it by pasting the link up here!
                    </Typography>                                                                    
                </CardContent>
              </Card>
          {!isLoading && Array.isArray(filteredLinks) && filteredLinks.map((link) => {
            const isPrivateAndNotOwner = !link.isPublic
            const displayName = isPrivateAndNotOwner ? 'Kast User' : link.userName;
            const avatarSrc = isPrivateAndNotOwner 
              ? AvatarIcon
              : link.profilePicture || `https://ui-avatars.com/api/?name=${link.userName}&background=FF890A&color=fff`;

            return (
              <Card 
                key={link.id} 
                sx={{ 
                  borderRadius: 4, 
                  backgroundColor: '#fefaf5', 
                  border: '1px solid #74706B', // Changed to dark grey
                  boxShadow: 'none' // Removed shadow
                }}
              >
                <CardContent>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    mb: 2 
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar 
                        src={avatarSrc}
                        sx={{ width: 32, height: 32, mr: 1.5 }}
                      >
                        {displayName.charAt(0)}
                      </Avatar>
                      <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                        {displayName}
                      </Typography>
                    </Box>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ fontSize: '0.8rem' }} // Made timestamp bigger
                    >
                      {formatTimeAgo(link.timestamp)}
                    </Typography>
                  </Box>
                  
                  <Typography sx={{ 
                    fontWeight: 600, 
                    fontSize: isMobile ? '15px' : '18px' // Reduced title size by 2 units
                  }}>
                    {link.title || 'No title'}
                  </Typography>
                  
                  {link.description && (
                    <Typography variant="body2" color="text.secondary">
                      {link.description}
                    </Typography>
                  )}
                  
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    mt: 1
                  }}>
                    {link.siteName && (
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        fontWeight: 800, 
                        color: '#74706B',
                        fontSize: '14px' 
                      }}>
                        {link.siteName}
                      </Typography>
                    )} 
                    {link?.logo && (
                      <Avatar 
                        src={link.logo} 
                        sx={{ 
                          width: 40, 
                          height: 40
                        }}
                      />
                    )}               
                  </Box>
                  
                  {link.caption && (
                    <Typography variant="body2" sx={{ 
                      mb: 2, 
                      fontWeight: 400, 
                      fontSize: "14px", 
                      color: '#5a5753', // Darker color
                      fontStyle: 'italic' // Added italics
                    }}>
                      {link.caption}
                    </Typography>
                  )}

                  {/* Removed Divider */}
                  
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    gap: 5, 
                    height: 50
                  }}>
                    <Tooltip title="Like" arrow>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center', 
                          cursor: 'pointer',
                          lineHeight: 1
                        }}
                        onClick={() => fetchLikesForPost(link.id)}
                      >
                        <IconButton                           
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLikePost(link.id);
                          }}
                          sx={{ color: '#115BBA' }} 
                        >
                          {userLikes[link.id] ? (
                            <Favorite color="inherit" sx={{ fontSize: isMobile ? '25px' : '30px' }}/> 
                          ) : (
                            <FavoriteBorder color="inherit" sx={{ fontSize: isMobile ? '25px' : '30px' }}/> 
                          )}
                        </IconButton>
                        <Typography 
                          variant="caption"
                          onClick={(e) => {
                            e.stopPropagation();
                            fetchLikesForPost(link.id);
                          }}
                          sx={{ fontSize: 10, mt: -0.5 }}
                        >
                          {link.likes || 0}
                        </Typography>
                      </Box>
                    </Tooltip>

                    <Tooltip title="Save" arrow>
                      <IconButton                         
                        onClick={() => handleSavePost(link.id)} 
                        sx={{ color: '#115BBA', mt: -1 }} 
                      >
                        {userSaves[link.id] ? (
                          <Bookmark color="inherit" sx={{ fontSize: isMobile ? '25px' : '30px' }} /> 
                        ) : (
                          <BookmarkBorder color="inherit" sx={{ fontSize: isMobile ? '25px' : '30px' }} /> 
                        )}
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Open" arrow>
                      <IconButton 
                        size="large" 
                        onClick={() => window.open(link.url, '_blank')} 
                        sx={{ color: '#115BBA', mt: -1 }} // Dark blue color
                      >
                        <LaunchIcon sx={{ fontSize: isMobile ? '25px' : '30px' }}/> 
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
          )} 
      <Layout>                   
    </Layout>
    </Container>  
  );
};

export default Home;